import { useState } from 'react';
import { motion } from 'framer-motion';
import { api } from '@/services/api';
import Button from '@/components/common/Button';

/**
 * MFA challenge step shown inline within the Login flow.
 *
 * Receives the short-lived `mfa_pending_token` from a successful password
 * login, prompts for a 6-digit TOTP or 8-char backup code, and posts to
 * /auth/mfa/challenge. On success calls onSuccess() with the full session
 * token + user — the parent (Login.tsx) is responsible for storing them.
 */

interface ChallengeResponse {
  token: string;
  /** Refreshable console session (P4-07). */
  refresh_token?: string;
  user: {
    id: string;
    email: string;
    name: string;
    phone?: string;
    job_title?: string;
    avatar_url?: string;
    onboarding_completed_at?: string | null;
    is_platform_admin?: boolean;
  };
}

interface Props {
  mfaPendingToken: string;
  /** When the user already enrolled — for messaging only. False = policy-required
   *  but no enrollment yet (caller should route to /account/mfa-setup instead). */
  enrolled: boolean;
  onSuccess: (resp: ChallengeResponse) => void;
  onCancel: () => void;
}

export default function MfaChallenge({ mfaPendingToken, enrolled, onSuccess, onCancel }: Props) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!enrolled) {
    // Policy requires MFA but the user hasn't enrolled. They cannot complete
    // login until they enroll — but enrollment requires authentication.
    // This is rare (admin set policy after the user already had a session) —
    // the cleanest path is to direct them to support.
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl p-6 backdrop-blur-xl border border-amber-400/30 bg-amber-500/5"
      >
        <h2 className="text-lg font-semibold text-amber-100 mb-2">MFA required</h2>
        <p className="text-sm text-amber-100/80 mb-4">
          Your organization requires two-factor authentication, but it isn't set
          up on your account yet. Contact your admin to complete enrollment.
        </p>
        <Button variant="outline" fullWidth onClick={onCancel}>
          Back to Sign-In
        </Button>
      </motion.div>
    );
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = code.replace(/\s/g, '');
    if (trimmed.length < 6) return;
    setError(null);
    setSubmitting(true);
    try {
      const resp = await api.post<ChallengeResponse>('/auth/mfa/challenge', {
        mfa_pending_token: mfaPendingToken,
        code: trimmed,
      });
      onSuccess(resp);
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error?.message;
      if (status === 401) {
        setError(msg || 'Code is incorrect. Try again.');
      } else if (status === 429) {
        setError('Too many attempts. Wait a minute and try again.');
      } else {
        setError(msg || 'Could not verify code.');
      }
      setSubmitting(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-6 backdrop-blur-xl border border-white/10 bg-white/5"
    >
      <h2 className="text-lg font-semibold text-white mb-1">Two-factor authentication</h2>
      <p id="mfa-code-hint" className="text-sm text-gray-300 mb-4">
        Enter the 6-digit code from your authenticator app. You can also use a
        backup code.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        {/*
          This field had no label and no aria-label -- only a "123456"
          placeholder, which disappears the moment anything is typed. A screen
          reader announced it as bare "edit text" at the two-factor gate, a
          screen nobody can skip. The visible paragraph above is the real
          instruction, so it is wired up as the description.
        */}
        <input
          autoFocus
          aria-label="Authentication code"
          aria-describedby="mfa-code-hint"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={submitting}
          placeholder="123456"
          maxLength={16}
          className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-center text-xl tracking-widest placeholder-gray-500 focus:outline-none focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20 disabled:opacity-50"
        />
        {error && (
          <div
            role="alert"
            className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2"
          >
            {error}
          </div>
        )}
        <Button
          type="submit"
          variant="primary"
          fullWidth
          disabled={submitting || code.replace(/\s/g, '').length < 6}
        >
          {submitting ? 'Verifying…' : 'Verify and Sign In'}
        </Button>
        <Button type="button" variant="ghost" fullWidth onClick={onCancel}>
          Cancel
        </Button>
      </form>
    </motion.div>
  );
}
